import { FILTER_TYPES, COMMON_EMAIL_DOMAINS } from './constants';

export const updateFilterState = (prevState, type, value, isMultiSelect = false) => {
  if (!isMultiSelect) {
    return {
      ...prevState,
      [type]: value
    };
  }

  // Handle array updates for multi-select filters
  const currentValues = prevState[type];
  let newValues;

  if (type === FILTER_TYPES.ORGANIZATION) {
    const index = currentValues.findIndex(item => item.id === value.id);
    newValues = index === -1 ? 
      [...currentValues, value] : 
      currentValues.filter(item => item.id !== value.id);
  } else {
    const index = currentValues.indexOf(value);
    newValues = index === -1 ? 
      [...currentValues, value] : 
      currentValues.filter(item => item !== value);
  }

  return {
    ...prevState,
    [type]: newValues
  };
};

export const resetFilterState = () => ({
  [FILTER_TYPES.ORGANIZATION]: [],
  [FILTER_TYPES.ROLE]: [],
  [FILTER_TYPES.LOCATION]: [],
  [FILTER_TYPES.STATUS]: [],
  [FILTER_TYPES.DOMAIN]: [],
  [FILTER_TYPES.SORT]: null
});

export const calculateCounts = (profiles, excludeFilter = null, currentFilters = {}) => {
  if (!profiles?.length) return {};

  const getFilteredProfiles = () => {
    let filtered = [...profiles];
    
    Object.entries(currentFilters).forEach(([type, value]) => {
      if (!value || type === excludeFilter) return;

      switch (type) {
        case FILTER_TYPES.ORGANIZATION:
          if (Array.isArray(value)) {
            filtered = filtered.filter(p => 
              value.some(org => org.id === p.organization_id)
            );
          }
          break;
        case FILTER_TYPES.ROLE:
          if (Array.isArray(value)) {
            filtered = filtered.filter(p => {
              if (value.includes('Women Founder')) {
                return value.includes(p.profiledata.userRoles) || 
                  (p.profiledata.userRoles === 'Founder' && p.profiledata.gender === 'Female');
              }
              return value.includes(p.profiledata.userRoles);
            });
          }
          break;
        case FILTER_TYPES.LOCATION:
          if (Array.isArray(value)) {
            filtered = filtered.filter(p => 
              value.includes(p.profiledata.userLocation)
            );
          }
          break;
        case FILTER_TYPES.STATUS:
          if (Array.isArray(value)) {
            filtered = filtered.filter(p => 
              value.includes(p.status)
            );
          }
          break;
        case FILTER_TYPES.DOMAIN:
          if (Array.isArray(value)) {
            filtered = filtered.filter(p => {
              const email = p.email.toLowerCase();
              const isCommonProvider = COMMON_EMAIL_DOMAINS.some(domain => 
                email.endsWith(domain)
              );
              return value.includes(isCommonProvider ? 'Common Email Providers' : 'Custom');
            });
          }
          break;
      }
    });
    
    return filtered;
  };

  const filteredProfiles = getFilteredProfiles();
  
  // Calculate all counts
  const locationCounts = { 'All Locations': filteredProfiles.length };
  const statusCounts = { 'All Status': filteredProfiles.length };
  const orgCounts = { 'All Organizations': filteredProfiles.length };
  const roleCounts = { 'All Roles': filteredProfiles.length };
  const domainCounts = { 'All Domains': filteredProfiles.length };

  // Calculate individual counts
  filteredProfiles.forEach(profile => {
    // Location counts
    const location = profile.profiledata.userLocation;
    locationCounts[location] = (locationCounts[location] || 0) + 1;

    // Status counts
    const status = profile.status;
    statusCounts[status] = (statusCounts[status] || 0) + 1;

    // Organization counts
    const orgId = profile.organization_id;
    orgCounts[orgId] = (orgCounts[orgId] || 0) + 1;

    // Role counts
    const role = profile.profiledata.userRoles;
    roleCounts[role] = (roleCounts[role] || 0) + 1;
    if (role === 'Founder' && profile.profiledata.gender === 'Female') {
      roleCounts['Women Founder'] = (roleCounts['Women Founder'] || 0) + 1;
    }

    // Domain counts
    const email = profile.email.toLowerCase();
    const isCommonProvider = COMMON_EMAIL_DOMAINS.some(domain => email.endsWith(domain));
    const domainType = isCommonProvider ? 'Common Email Providers' : 'Custom';
    domainCounts[domainType] = (domainCounts[domainType] || 0) + 1;
  });

  return {
    locationCounts,
    statusCounts,
    orgCounts,
    roleCounts,
    domainCounts
  };
};

export const calculateMetrics = (profiles, currentFilters = {}) => {
  if (!profiles?.length) return {};

  // First apply all filters except status and created_at
  const baseFiltered = profiles.filter(profile => {
    let passes = true;
    
    Object.entries(currentFilters).forEach(([type, value]) => {
      if (!value || type === 'created_at' || type === FILTER_TYPES.STATUS) return;

      switch (type) {
        case FILTER_TYPES.ORGANIZATION:
          if (Array.isArray(value)) {
            passes = passes && value.some(org => org.id === profile.organization_id);
          }
          break;
        case FILTER_TYPES.ROLE:
          if (Array.isArray(value)) {
            if (value.includes('Women Founder')) {
              passes = passes && (
                value.includes(profile.profiledata.userRoles) || 
                (profile.profiledata.userRoles === 'Founder' && profile.profiledata.gender === 'Female')
              );
            } else {
              passes = passes && value.includes(profile.profiledata.userRoles);
            }
          }
          break;
        case FILTER_TYPES.LOCATION:
          if (Array.isArray(value)) {
            passes = passes && value.includes(profile.profiledata.userLocation);
          }
          break;
        case FILTER_TYPES.DOMAIN:
          if (Array.isArray(value)) {
            const email = profile.email.toLowerCase();
            const isCommonProvider = COMMON_EMAIL_DOMAINS.some(domain => 
              email.endsWith(domain)
            );
            passes = passes && value.includes(
              isCommonProvider ? 'Common Email Providers' : 'Custom'
            );
          }
          break;
      }
    });

    return passes;
  });

  // Then apply date filter if exists
  const dateFiltered = currentFilters.created_at ? 
    baseFiltered.filter(profile => new Date(profile.created_at) > new Date(currentFilters.created_at)) :
    baseFiltered;

  // Finally apply status filter if exists
  const statusFiltered = currentFilters[FILTER_TYPES.STATUS]?.length ? 
    dateFiltered.filter(profile => currentFilters[FILTER_TYPES.STATUS].includes(profile.status)) :
    dateFiltered;

  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  // Calculate metrics based on the filtered profiles
  return {
    // Total is based on status-filtered profiles
    total: statusFiltered.length,
    // New this week is based on status-filtered profiles
    newThisWeek: statusFiltered.filter(profile => 
      new Date(profile.created_at) > oneWeekAgo
    ).length,
    // Status counts are based on date-filtered profiles (before status filter)
    submitted: dateFiltered.filter(profile => profile.status === 'submitted').length,
    active: dateFiltered.filter(profile => profile.status === 'active').length,
    waitlist: dateFiltered.filter(profile => profile.status === 'waitlist').length
  };
}; 